import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import { CRUISE_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import * as isomorphicSessionStorage from 'lib/storage/isomorphicSessionStorage'
import { UniqueItineraryItem } from 'components/Cruises/CruiseOfferPage/content/Itinerary/CruiseItinerary'
import { trackClientEvent } from 'analytics/analytics'
import { CABIN_CATEGORIES, CABIN_CATEGORIES_RIVER, CruiseType } from 'constants/cruise'

export const titleGenderMap = {
  Sr: 'M',
  Mstr: 'M',
  Mr: 'M',
  Ms: 'F',
  Mrs: 'F',
  Miss: 'F',
}

export function getCruiseDurationByItinerary(itinerary?: Array<App.CruiseItineraryItem>): number {
  const uniqueItineraryDays = new Set<number>()
  itinerary?.forEach(item => uniqueItineraryDays.add(item.startDay))

  return uniqueItineraryDays.size
}

export function getCruiseCountriesByItinerary(itinerary?: Array<App.CruiseItineraryItem>): Array<string> {
  const countries = (itinerary ?? []).map(({ port }) => port?.countryCode).filter(Boolean) as Array<string>
  return countries?.length ? unique(countries) : []
}

export function getCruisePortsByItinerary(itinerary?: Array<App.CruiseItineraryItem>): Array<string> {
  const cities = (itinerary ?? []).map(({ port }) => {
    if (!port) return

    const portName = port.name.split(',')[0]
    if (port.city?.trim() === '') return portName

    return port.city ?? portName
  }).filter(Boolean) as Array<string>

  return cities?.length ? unique(cities) : []
}

export function offerHasValidDepartures(offer: App.CruiseOffer): boolean {
  return (offer.departureDates ?? []).filter(d => moment(new Date(d)) >= moment()).length > 0
}

type PricingSchedule = Partial<CruisesContract.PricingCancellationSchedule> & Partial<CruisesContract.PricingPaymentSchedule>
export function filterDataByHighestPercentage(data: Array<PricingSchedule>, isNonRefundable: boolean) {
  const areAllDatesEqual = data.every(obj => obj.date === data[0].date)
  let filteredData = data

  if (areAllDatesEqual) {
    filteredData = data.filter(obj => obj.isPercentage)
  }

  const finalResult: Record<string, App.PricingCancellationSchedule> = {}

  filteredData.forEach(schedule => {
    const date = schedule.date
    const amount = schedule.amount
    if (date && amount && (!finalResult[date] || amount > (finalResult[date].amount ?? 0))) {
      finalResult[date] = {
        ...schedule,
        isNonRefundable: !!isNonRefundable,
      } as App.PricingCancellationSchedule
    }
  })

  return Object.values(finalResult)
}

export interface CruiseOfferPriceVariation {
  primaryPrice: {
    total: number
    type: string
  }
  secondaryPrice?: {
    total: number
    type: string
  }
}

export function getCruiseOfferPriceVariation(
  offer: App.CruiseOffer,
  price?: App.CruisePrice | App.LowestPriceWithCategory | App.CruisePriceDetails,
  isPricePerNight?: boolean,
): CruiseOfferPriceVariation {
  if (!price) {
    return {
      primaryPrice: {
        total: 0,
        type: 'person',
      },
    }
  }

  const pricePerPerson = {
    total: price.total,
    type: 'person',
  }

  if (offer.showPricePerNight || isPricePerNight) {
    const pricePerNight = {
      total: Math.round(price.total / offer.duration),
      type: 'night',
    }

    return {
      primaryPrice: pricePerNight,
      secondaryPrice: pricePerPerson,
    }
  }

  return {
    primaryPrice: pricePerPerson,
  }
}

export interface CruiseOfferPriceVariationNew {
  primaryPrice?: App.Pricing
  secondaryPrice?: App.Pricing
}

export function getCruiseOfferPriceVariationNew(
  offer: App.CruiseOffer,
  price?: App.CruisePrice | App.LowestPriceWithCategory | App.CruisePriceDetails,
  isPricePerNight?: boolean,
): CruiseOfferPriceVariationNew {
  if (!price) {
    return {}
  }

  let pricePerPerson: App.Pricing = {
    price: price.total,
    saleUnit: 'person',
    fees: [],
  }

  if ('discountPills' in price && price.discountPills) {
    pricePerPerson = {
      ...pricePerPerson,
      value: price.discountPills.price,
      discountPercent: price.discountPills.discountPercentage,
    }
  }

  if (offer.showPricePerNight || isPricePerNight) {
    const pricePerNight: App.Pricing = {
      price: Math.round(price.total / offer.duration),
      saleUnit: 'night',
      fees: [],
    }

    return {
      primaryPrice: pricePerNight,
      secondaryPrice: pricePerPerson,
    }
  }

  return {
    primaryPrice: pricePerPerson,
  }
}

export function getItineraryUniqueKey(itinerary: App.CruiseItineraryItem): string {
  const { title, startDay, startTime, endTime } = itinerary
  return `${title}-${startDay}-${startTime}-${endTime}`
}

// ----------------------  Search Utils ----------------------
function queryParamsToSearchItem(
  queryParams: URLSearchParams,
  idKey: string,
  nameKey: string,
): App.SearchItem | undefined {
  const id = queryParams.get(idKey)
  const name = queryParams.get(nameKey)
  if (id && name) {
    return {
      searchType: 'destination',
      value: id,
      format: {
        mainText: name,
      },
    }
  }
}

function queryParamsToSearchItems(
  queryParams: URLSearchParams,
  idsKey: string,
  namesKey: string,
): Array<App.SearchItem> {
  const searchPlaceIds = queryParams.getAll(idsKey)
  const searchNames = queryParams.getAll(namesKey)

  return searchPlaceIds.map((placeId, index) => {
    return {
      searchType: 'destination',
      value: placeId,
      format: {
        mainText: searchNames[index],
      },
    }
  })
}

export function getCruiseSearchStateFromParams(queryParams: URLSearchParams): Partial<GlobalSearchState> {
  const durationMin = queryParams.get('durationMin')
  const durationMax = queryParams.get('durationMax')
  const durationRange = queryParams.getAll('durationRange') as unknown as Array<CRUISE_FLEXIBLE_DURATION_RANGE>
  return {
    searchItem: queryParamsToSearchItem(queryParams, 'destinationId', 'destinationName'),
    searchItems: queryParamsToSearchItems(queryParams, 'destinationIds', 'destinationNames'),
    secondarySearchItem: queryParamsToSearchItem(queryParams, 'departureId', 'departureName'),
    secondarySearchItems: queryParamsToSearchItems(queryParams, 'departureIds', 'departureNames'),
    cruiseLines: queryParams.getAll('cruiseLines'),
    flexibleMonths: queryParams.get('departureMonths') ?? undefined,
    durationMin: durationMin ? parseInt(durationMin) : undefined,
    durationMax: durationMax ? parseInt(durationMax) : undefined,
    durationRange: durationRange ?? undefined,
    checkinDate: queryParams.get('checkinDate') ? moment(queryParams.get('checkinDate')) : undefined,
    checkoutDate: queryParams.get('checkoutDate') ? moment(queryParams.get('checkoutDate')) : undefined,
  }
}

export function getCruiseOfferFiltersFromParams(queryParams: URLSearchParams): [App.OfferListFilters, boolean] {
  const minPrice = queryParams.get('minPrice')
  const maxPrice = queryParams.get('maxPrice')
  const durationMin = queryParams.get('durationMin')
  const durationMax = queryParams.get('durationMax')
  const cruiseShips = queryParams.getAll('cruiseShips')
  const cabinTypes = queryParams.getAll('cabinTypes')
  const isLuxExclusivePromotion = Boolean(queryParams.get('isLuxExclusivePromotion'))
  const isSpecialOffers = Boolean(queryParams.get('isSpecialOffers'))
  const isLuxPlus = Boolean(queryParams.get('isLuxPlus'))
  const cruiseTypes = queryParams.getAll('cruiseTypes')
  const filtersApplied = Boolean(minPrice || maxPrice || cruiseShips.length || cabinTypes.length || isLuxExclusivePromotion || isSpecialOffers || isLuxPlus || cruiseTypes.length)

  return [{
    offerTypes: ['cruise'],
    destinationIds: queryParams.getAll('destinationIds'),
    destinationNames: queryParams.getAll('destinationNames'),
    destinationId: queryParams.get('destinationId') ?? undefined,
    destinationName: queryParams.get('destinationName') ?? undefined,
    departureName: queryParams.get('departureName') ?? undefined,
    departurePlaceId: queryParams.get('departureId') ?? undefined,
    departureIds: queryParams.getAll('departureIds'),
    departureNames: queryParams.getAll('departureNames'),
    durationMin: durationMin ? parseInt(durationMin) : undefined,
    durationMax: durationMax ? parseInt(durationMax) : undefined,
    durationRange: queryParams.getAll('durationRange') ?? undefined,
    departureStartDate: queryParams.get('checkinDate') ?? undefined,
    departureEndDate: queryParams.get('checkoutDate') ?? undefined,
    cruiseLines: queryParams.getAll('cruiseLines'),
    departureMonths: queryParams.get('departureMonths')?.split(','),
    rateCodes: queryParams.getAll('rateCodes'),
    cruiseShips,
    cabinTypes,
    isSpecialOffers,
    isLuxExclusivePromotion,
    isLuxPlus,
    cruiseTypes,
    minPrice: minPrice ? parseInt(minPrice) : undefined,
    maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
    sortBy: queryParams.get('sortBy') as App.OfferListSortByOptions ?? undefined,
    priceBy: queryParams.get('priceBy') ?? undefined,
    campaigns: queryParams.getAll('campaigns'),
  }, filtersApplied]
}

export function getCruiseExtraFilters(queryParams: URLSearchParams, globalSearch?: GlobalSearchState) {
  const cruiseShips = queryParams.getAll('cruiseShips')
  const minPrice = queryParams.get('minPrice')
  const maxPrice = queryParams.get('maxPrice')
  const isSpecialOffers = queryParams.get('isSpecialOffers')
  const isLuxExclusivePromotion = queryParams.get('isLuxExclusivePromotion')
  const isLuxPlus = queryParams.get('isLuxPlus')
  const sortBy = queryParams.get('sortBy')
  const cruiseTypes = queryParams.getAll('cruiseTypes')

  return {
    cruiseShips,
    cabinTypes: queryParams.getAll('cabinTypes'),
    isSpecialOffers: Boolean(isSpecialOffers) || undefined,
    isLuxExclusivePromotion: Boolean(isLuxExclusivePromotion) || undefined,
    isLuxPlus: Boolean(isLuxPlus) || undefined,
    sortBy: sortBy || undefined,
    priceBy: queryParams.get('priceBy') ?? undefined,
    minPrice: minPrice ? parseInt(minPrice) : undefined,
    maxPrice: maxPrice ? parseInt(maxPrice) : undefined,
    durationMin: queryParams.get('durationMin') ?? undefined,
    durationMax: queryParams.get('durationMax') ?? undefined,
    durationRange: globalSearch?.durationRange ?? queryParams.getAll('durationRange') ?? undefined,
    campaigns: queryParams.getAll('campaigns') ?? undefined,
    cruiseTypes,
  }
}

export function getCruiseGlobalFilters(globalSearch: GlobalSearchState) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    secondarySearchItems,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    checkinDate,
    checkoutDate,
  } = globalSearch

  return {
    cruiseLines,
    durationMax: durationMax || undefined,
    durationMin: durationMin || undefined,
    durationRange: durationRange || undefined,
    destinationId: searchItem?.value,
    destinationName: searchItem?.format.mainText,
    departureId: secondarySearchItem?.value,
    departureName: secondarySearchItem?.format.mainText,
    departureMonths: flexibleMonths || undefined,
    destinationNames: searchItems?.map((item) => item.format.mainText),
    destinationIds: searchItems?.map((item) => item.value),
    departureNames: secondarySearchItems?.map((item) => item.format.mainText),
    departureIds: secondarySearchItems?.map((item) => item.value),
    ...(!!checkinDate && !!checkoutDate && {
      checkinDate: checkinDate?.format(ISO_DATE_FORMAT),
      checkoutDate: checkoutDate?.format(ISO_DATE_FORMAT),
      departureStartDate: checkinDate?.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate?.format(ISO_DATE_FORMAT),
    }),
  }
}

export function getGlobalFacetFilters(globalSearch: GlobalSearchState) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    secondarySearchItems,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
    checkinDate,
    checkoutDate,
    durationRange,
  } = globalSearch

  const departureMonths = flexibleMonths?.split(',').filter(Boolean) || EmptyArray

  return {
    cruiseLines,
    durationMax: durationMax || undefined,
    durationMin: durationMin || undefined,
    destinationId: searchItem?.value,
    destinationName: searchItem?.format.mainText,
    departureId: secondarySearchItem?.value,
    departureName: secondarySearchItem?.format.mainText,
    destinationNames: searchItems.map((item) => item.format.mainText),
    destinationIds: searchItems.map((item) => item.value),
    departureNames: secondarySearchItems.map((item) => item.format.mainText),
    departureIds: secondarySearchItems.map((item) => item.value),
    ...(departureMonths.length && { departureMonths }),
    ...(!!checkinDate && !!checkoutDate && {
      checkinDate: checkinDate.format(ISO_DATE_FORMAT),
      checkoutDate: checkoutDate.format(ISO_DATE_FORMAT),
      departureStartDate: checkinDate.format(ISO_DATE_FORMAT),
      departureEndDate: checkoutDate.format(ISO_DATE_FORMAT),
    }),
    ...(durationRange?.length && { durationRange }),
  }
}

export function getCruiseFilters(globalSearch: GlobalSearchState, queryParams?: URLSearchParams) {
  const globalFilters = getCruiseGlobalFilters(globalSearch)

  return {
    ...globalFilters,
    ...(queryParams ? getCruiseExtraFilters(queryParams, globalSearch) : {}),
  }
}

export function getCruiseSearchFiltersBar(
  values: App.CruiseGlobalFilters,
  globalSearch: GlobalSearchState,
  pathname: string,
  regionCode: string,
  forceSearchPage = true,
) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    flexibleMonths,
    durationMax,
    durationMin,
    durationRange,
    cruiseLines,
    checkinDate,
    checkoutDate,
  } = values

  const filters: App.CruiseGlobalFilters = {
    ...globalSearch,
    ...(cruiseLines && { cruiseLines }),
  }

  if ('durationMin' in values) {
    filters.durationMin = durationMin
  }

  if ('durationMax' in values) {
    filters.durationMax = durationMax
  }

  if ('durationRange' in values) {
    filters.durationRange = durationRange
  }

  if ('flexibleMonths' in values) {
    filters.flexibleMonths = flexibleMonths
  }

  if ('searchItem' in values) {
    filters.searchItem = searchItem
  }

  if ('searchItems' in values) {
    filters.searchItems = searchItems
  }

  if ('secondarySearchItem' in values) {
    filters.secondarySearchItem = secondarySearchItem
  }

  if ('checkinDate' in values) {
    filters.checkinDate = checkinDate
  }

  if ('checkoutDate' in values) {
    filters.checkoutDate = checkoutDate
  }

  const pageName = forceSearchPage ? '/search/cruises' : pathname.replace(`/${regionCode.toLowerCase()}`, '')
  return {
    pageName,
    filters: filters as GlobalSearchState,
  }
}

export function resetCruiseTimer() {
  const time = getCruiseVendorBookingTimer()
  // the vendor sends the length of time as seconds thats why we have this "time/60"
  const expirationTimestamp = moment().add(time, 'minutes').toDate().getTime()

  isomorphicSessionStorage.set('cruise-timer-expiration-timestamp', expirationTimestamp)
}

export function expireCruiseTimer() {
  isomorphicSessionStorage.set('cruise-timer-expiration-timestamp', 0)
}

export function setCruiseVendorBookingTimer(time: number) {
  isomorphicSessionStorage.set('cruise-vendor-booking-timer', time)
}

export function getCruiseVendorBookingTimer() {
  return Number(isomorphicSessionStorage.get('cruise-vendor-booking-timer')) / 60
}

export function getCruiseTimerExpirationTimestamp() {
  return Number(isomorphicSessionStorage.get('cruise-timer-expiration-timestamp'))
}

export function getCruiseLuxPlusItems(facets: Array<App.CruiseSearchFacet>): Array<App.CruiseSearchFacet> {
  return facets
    .filter(facet => facet.category === 'lux_plus')
    .map(facet => ({
      ...facet,
      name: 'Bonus inclusions',
    }))
}

export function countCruiseItemsPassengers(items: Array<App.OrderCruiseItem>): {
  adults: number
  children: number
} {
  const { adults, children } = items.reduce((acc, item) => {
    acc.adults += item.passengers.filter(passenger => passenger.type === 'adult').length
    acc.children += item.passengers.filter(passenger => passenger.type === 'child').length
    return acc
  }, { adults: 0, children: 0 })

  return {
    adults,
    children,
  }
}

export function getCruiseUniqueItineraryList(
  itinerary: Array<App.CruiseItineraryItem>,
  showOnlyPorts: boolean,
  portNameSelected?: string | null,
  itineraryKeySelected?: string | null,
) {
  if (!itinerary) return []

  const itineraryWithoutRepeatedPorts: Array<App.CruiseItineraryItem> = Object.values(itinerary.reduce((acc, item) => ({
    ...acc,
    [`${item.startDay}_${item.port?.name || 'at_sea'}`]: item,
  }), {}))

  let portNumber = 0
  let lastPortName = ''

  return Object.values(itineraryWithoutRepeatedPorts.reduce<UniqueItineraryItem>((acc, item, index) => {
    if (showOnlyPorts && !item.port) return acc

    const portName = item.port?.name || 'at_sea'
    const key = `${item.startDay}_${portName}`
    const isStart = index === 0
    const isEnd = index === itineraryWithoutRepeatedPorts.length - 1

    // add port circle if port is different from previous item
    const addPortCircle = !!item.port && lastPortName !== item.port?.name || isStart
    const itineraryKey = getItineraryUniqueKey(item)

    const isSelected = portNameSelected === portName && (!itineraryKeySelected || itineraryKeySelected === itineraryKey)

    const result = {
      ...acc,
      [key]: {
        portName,
        item,
        portNumber,
        addPortCircle,
        isStart,
        isEnd,
        itineraryKey,
        isSelected,
      },
    }

    if (addPortCircle) {
      portNumber += 1
      lastPortName = item.port?.name || ''
    }

    return result
  }, {}))
}

export function trackCruisesClickEvent(subject: string) {
  trackClientEvent({
    subject,
    action: 'clicked',
    category: 'cruise',
    type: 'interaction',
  })
}

export function getCabinCategories(cruiseType?: App.CruiseType) {
  if (cruiseType === CruiseType.RIVER) {
    return CABIN_CATEGORIES_RIVER
  }

  return CABIN_CATEGORIES
}
